import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Check, MessageCircle, Trash2, Flag, X } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function ResolveModal({ report, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/mod-reports/${report.id}/resolve`, { admin_note: note.trim() });
      toast.success("Report resolved");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" data-testid="resolve-mod-report-modal">
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] max-w-md w-full p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-2">// resolve mod report</div>
        <h3 className="font-heading text-xl uppercase mb-4">{report.reported_username}</h3>
        <p className="text-xs text-[#71717A] mb-4 font-mono">Reason: "{report.reason}"</p>
        <textarea
          data-testid="resolve-mod-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Optional note to the reporting moderator…"
          className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] resize-none text-[#09090B] dark:text-[#F4F4F5]"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-xs uppercase tracking-widest hover:border-[#E11D48] hover:text-[#E11D48] transition-colors font-body">Cancel</button>
          <button data-testid="confirm-resolve-mod" onClick={submit} disabled={busy} className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-5 py-2 text-xs hover:bg-[#6D28D9] transition-colors disabled:opacity-50">{busy ? "Saving…" : "Resolve"}</button>
        </div>
      </div>
    </div>
  );
}

function RemoveConfirmModal({ report, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${report.reported_user_id}`);
      toast.success(`${report.reported_username} removed from ePlay`);
      // Auto-resolve the report too
      try { await api.post(`/admin/mod-reports/${report.id}/resolve`, { admin_note: "User removed from the app." }); } catch (e) { /* ignore */ }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" data-testid="remove-user-modal">
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#E11D48] max-w-md w-full p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48] mb-2">// danger zone</div>
        <h3 className="font-heading text-xl uppercase mb-4">Remove {report.reported_username}?</h3>
        <p className="text-sm text-[#71717A] font-body mb-4">
          This permanently removes the user from ePlay, deletes all their fixtures, and wipes their DMs. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-xs uppercase tracking-widest hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors font-body">Cancel</button>
          <button data-testid="confirm-remove-user" onClick={remove} disabled={busy} className="bg-[#E11D48] text-white font-heading uppercase tracking-widest px-5 py-2 text-xs hover:bg-[#BE123C] transition-colors disabled:opacity-50">{busy ? "Removing…" : "Remove User"}</button>
        </div>
      </div>
    </div>
  );
}

export default function ModReportsTab() {
  const [data, setData] = useState({ reports: [], open_count: 0, resolved_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [resolveTarget, setResolveTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const refresh = async () => {
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/admin/mod-reports${params}`);
      setData(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); refresh(); }, [filter]);

  return (
    <div className="space-y-6" data-testid="mod-reports-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C] mb-1">// reported by moderators</div>
          <h2 className="font-heading text-3xl uppercase tracking-wider">Mod Reports</h2>
          <div className="font-mono text-[10px] text-[#71717A] mt-1">
            {data.open_count} open · {data.resolved_count} resolved
          </div>
        </div>
        <div className="flex gap-1" data-testid="mod-reports-filter">
          {["open", "resolved", "all"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] border transition-colors ${filter === f ? "border-[#7C3AED] text-[#7C3AED]" : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#A1A1AA] hover:text-[#7C3AED]"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <TactLoader />
      ) : data.reports.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ NO {filter.toUpperCase()} MOD REPORTS. ]</Panel>
      ) : (
        <div className="space-y-3" data-testid="mod-reports-list">
          {data.reports.map((r) => {
            const isOpen = r.status === "open";
            return (
              <div
                key={r.id}
                data-testid={`mod-report-${r.id}`}
                className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 ${isOpen ? "border-l-[#EA580C]" : "border-l-[#34C759]"} p-5`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Flag className={`w-3.5 h-3.5 ${isOpen ? "text-[#EA580C]" : "text-[#34C759]"}`} />
                      <span className="font-heading text-lg uppercase text-[#09090B] dark:text-[#F4F4F5]">{r.reported_username}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                        reported by <span className="text-[#7C3AED]">{r.moderator_username}</span>
                      </span>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border ${isOpen ? "text-[#EA580C] border-[#EA580C]" : "text-[#34C759] border-[#34C759]"}`}>
                        {r.status}
                      </span>
                    </div>
                    <p className="text-sm text-[#09090B] dark:text-[#F4F4F5] font-body whitespace-pre-wrap break-words">{r.reason}</p>
                    <div className="font-mono text-[10px] tracking-[0.2em] text-[#71717A] mt-2">
                      Filed {fmt(r.created_at)}
                      {r.resolved_at && <> · Resolved {fmt(r.resolved_at)}</>}
                    </div>
                    {r.admin_note && (
                      <div className="mt-2 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] p-2 text-xs font-body">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">// admin note</span>
                        <div className="mt-1 text-[#09090B] dark:text-[#F4F4F5]">{r.admin_note}</div>
                      </div>
                    )}
                  </div>
                  {isOpen && (
                    <div className="flex gap-2 flex-wrap">
                      <button
                        data-testid={`resolve-btn-${r.id}`}
                        onClick={() => setResolveTarget(r)}
                        className="inline-flex items-center gap-1 bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 hover:bg-[#6D28D9] transition-colors"
                      >
                        <Check className="w-3 h-3" /> Resolve
                      </button>
                      <Link
                        to={`/chat/${r.reported_user_id}`}
                        data-testid={`message-btn-${r.id}`}
                        className="inline-flex items-center gap-1 border border-[#7C3AED] text-[#7C3AED] font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 hover:bg-[#7C3AED] hover:text-white transition-colors"
                      >
                        <MessageCircle className="w-3 h-3" /> Message
                      </Link>
                      <button
                        data-testid={`remove-btn-${r.id}`}
                        onClick={() => setRemoveTarget(r)}
                        className="inline-flex items-center gap-1 border border-[#E11D48] text-[#E11D48] font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 hover:bg-[#E11D48] hover:text-white transition-colors"
                      >
                        <Trash2 className="w-3 h-3" /> Remove from App
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {resolveTarget && <ResolveModal report={resolveTarget} onClose={() => setResolveTarget(null)} onDone={() => { setResolveTarget(null); refresh(); }} />}
      {removeTarget && <RemoveConfirmModal report={removeTarget} onClose={() => setRemoveTarget(null)} onDone={() => { setRemoveTarget(null); refresh(); }} />}
    </div>
  );
}
